import { afterNextRender, ChangeDetectionStrategy, Component, computed, effect, ElementRef, HostListener, inject, Injector, NgZone, OnDestroy, signal } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { SvgIconsComponent } from '../svg-icons/svg-icons.component';
import { ClockWidgetService } from './clock-widget.service';
import { ClockZone, clockReadings, restoreClockOrder } from './timezone-clock.util';

@Component({
  selector: 'app-usa-timezone-clock',
  standalone: true,
  imports: [SvgIconsComponent, CdkDropList, CdkDrag],
  templateUrl: './usa-timezone-clock.component.html',
  styleUrl: './usa-timezone-clock.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsaTimezoneClockComponent implements OnDestroy {
  readonly isVisible = signal(this.readStorage('clocksVisible') === 'true');
  readonly isCompact = signal(window.innerWidth <= 1100);
  readonly detailsOpen = signal(false);
  readonly clocksAreOpen = computed(() => (this.isCompact() ? this.detailsOpen() : this.isVisible()));
  readonly currentTime = signal(new Date());
  readonly timezones = signal<ClockZone[]>(this.loadOrder());
  readonly readings = computed(() => clockReadings(this.timezones(), this.currentTime(), this.clocks.baseZone()));
  readonly announcement = signal('');

  private readonly injector = inject(Injector);
  private timer?: ReturnType<typeof setTimeout>;
  private destroyed = false;
  private readonly visibilityChanged = () => this.syncClock();
  private readonly resized = () => {
    const compact = window.innerWidth <= 1100;
    if (compact !== this.isCompact()) {
      this.detailsOpen.set(false);
      this.isCompact.set(compact);
    }
  };
  private readonly outsidePointer = (event: PointerEvent) => {
    if (this.detailsOpen() && !this.elementRef.nativeElement.contains(event.target as Node)) {
      // Let the original click reach the work area without stealing input focus.
      this.detailsOpen.set(false);
    }
  };

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone,
    readonly clocks: ClockWidgetService
  ) {
    this.clocks.dateInClock.set(true);
    effect(() => {
      this.syncClock();
    });
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('visibilitychange', this.visibilityChanged);
      document.addEventListener('pointerdown', this.outsidePointer);
      window.addEventListener('resize', this.resized);
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clocks.dateInClock.set(false);
    clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.visibilityChanged);
    document.removeEventListener('pointerdown', this.outsidePointer);
    window.removeEventListener('resize', this.resized);
  }

  /** Only visible, expanded clocks need a minute-aligned update. */
  private syncClock(): void {
    clearTimeout(this.timer);
    if (this.destroyed || !this.clocksAreOpen() || document.hidden) return;
    this.currentTime.set(new Date());
    this.ngZone.runOutsideAngular(() => {
      this.timer = setTimeout(() => this.syncClock(), 60_000 - (Date.now() % 60_000) + 50);
    });
  }

  toggleVisibility(): void {
    if (this.isCompact()) {
      this.detailsOpen.update((value) => !value);
    } else {
      this.isVisible.update((value) => !value);
      this.writeStorage('clocksVisible', String(this.isVisible()));
    }
    this.focusAfterRender();
    this.syncClock();
  }

  @HostListener('keydown.escape', ['$event'])
  closeCompactView(event: KeyboardEvent): void {
    if (!this.isCompact() || !this.detailsOpen()) return;
    event.stopPropagation();
    this.detailsOpen.set(false);
    this.focusAfterRender();
  }

  private focusAfterRender(): void {
    afterNextRender(
      () => {
        if (this.destroyed) return;
        this.elementRef.nativeElement.querySelector<HTMLButtonElement>(this.clocksAreOpen() ? '.zone-select[aria-pressed="true"]' : '.widget-trigger')?.focus();
      },
      { injector: this.injector }
    );
  }

  setBaseZone(zone: string): void {
    this.clocks.setBaseZone(zone);
    const name = this.timezones().find((item) => item.zone === zone)?.name;
    if (name) this.announcement.set(`${name} selected as the footer timezone.`);
  }

  drop(event: CdkDragDrop<ClockZone[]>): void {
    this.reorder(event.previousIndex, event.currentIndex);
  }

  onZoneKeydown(event: KeyboardEvent, zone: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.setBaseZone(zone);
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const index = this.timezones().findIndex((item) => item.zone === zone);
    this.reorder(index, index + (event.key === 'ArrowLeft' ? -1 : 1));
  }

  private reorder(from: number, to: number): void {
    const zones = [...this.timezones()];
    if (from < 0 || to < 0 || to >= zones.length || from === to) return;
    moveItemInArray(zones, from, to);
    this.timezones.set(zones);
    this.writeStorage('clock_order_preference', JSON.stringify(zones));
    this.announcement.set(`${zones[to].name} moved to position ${to + 1} of ${zones.length}.`);
  }

  private loadOrder(): ClockZone[] {
    try {
      return restoreClockOrder(JSON.parse(this.readStorage('clock_order_preference') || 'null'));
    } catch {
      return restoreClockOrder(null);
    }
  }

  private readStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeStorage(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Browser preferences are optional; controls work without storage.
    }
  }
}
